import type { ReactNode } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { AuroraBackground } from './AuroraBackground';

interface StatusScreenProps {
  icon?: ReactNode;
  title: string;
  description: ReactNode;
  actions?: ReactNode;
}

/**
 * Full-page message for states outside the normal app: unknown address,
 * suspended space, no access, API unavailable. Uses the tenant's theme when
 * one is loaded, so even error pages keep the customer's identity.
 */
export function StatusScreen({ icon, title, description, actions }: StatusScreenProps) {
  return (
    <Box
      component="main"
      sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', px: 2, py: 4 }}
    >
      <AuroraBackground />
      <Card
        sx={(theme) => ({
          width: '100%',
          maxWidth: 480,
          borderRadius: `${Math.min(Number(theme.shape.borderRadius) * 2.4, 32)}px`,
        })}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2.5} sx={{ alignItems: 'flex-start' }}>
            {icon}
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
            <Typography color="text.secondary">{description}</Typography>
            {actions && (
              <Stack direction="row" spacing={1.5} sx={{ pt: 1, flexWrap: 'wrap', gap: 1.5 }}>
                {actions}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
